import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Badge,
  Flex,
  Icon,
  SimpleGrid,
} from '@chakra-ui/react';
import { FaTrophy } from 'react-icons/fa';

/**
 * Lista de logros centrada en rejilla, tarjetas con icono destacado.
 * `variant` alinea el aspecto con el tema del micrositio.
 */
export default function MicrositeLogrosBlock({ logros, primaryColor, surfaceColor, variant = 'moderno' }) {
  const list = Array.isArray(logros) ? logros.filter((l) => l && (l.titulo || l.descripcion || l.ano)) : [];
  if (!list.length) return null;

  const mdCols = list.length === 1 ? 1 : 2;

  const yearBadge = (ano) => {
    if (!ano) return null;
    if (variant === 'elegante') {
      return (
        <Text as="span" fontSize="xs" color="gray.400" fontWeight="500" letterSpacing="0.08em" textTransform="uppercase">
          {ano}
        </Text>
      );
    }
    if (variant === 'clasico') {
      return (
        <Badge colorPalette="gray" variant="subtle" fontSize="xs" rounded="md">
          {ano}
        </Badge>
      );
    }
    if (variant === 'aurora') {
      return (
        <Badge fontSize="xs" bg={`${primaryColor}22`} color="gray.100" borderWidth="1px" borderColor={`${primaryColor}55`} rounded="full">
          {ano}
        </Badge>
      );
    }
    if (variant === 'vortex') {
      return (
        <Badge fontSize="xs" bg={`${primaryColor}22`} color="cyan.100" borderWidth="1px" borderColor={`${primaryColor}66`} rounded="full">
          {ano}
        </Badge>
      );
    }
    if (variant === 'glass') {
      return (
        <Badge fontSize="xs" bg="whiteAlpha.700" color="gray.700" borderWidth="1px" borderColor="whiteAlpha.800" rounded="full">
          {ano}
        </Badge>
      );
    }
    if (variant === 'kinetic') {
      return (
        <Badge bg={primaryColor} color="white" fontSize="xs" fontWeight="800" rounded="full" px={3}>
          {ano}
        </Badge>
      );
    }
    if (variant === 'pulso') {
      return (
        <Badge bg={primaryColor} color="black" fontSize="xs" fontWeight="900" rounded="none" border="2px solid black" px={2}>
          {ano}
        </Badge>
      );
    }
    if (variant === 'editorial') {
      return (
        <Badge bg="gray.100" color="gray.800" fontSize="xs" fontWeight="700" rounded="none" px={2} py={0.5}>
          {ano}
        </Badge>
      );
    }
    return (
      <Badge
        bg={`${primaryColor}14`}
        color={primaryColor}
        fontSize="xs"
        fontWeight="700"
        rounded="full"
        px={3}
        py={0.5}
        borderWidth="1px"
        borderColor={`${primaryColor}33`}
      >
        {ano}
      </Badge>
    );
  };

  const renderTrophyIcon = () => {
    if (variant === 'elegante') {
      return <Icon color={primaryColor} boxSize={10} opacity={0.9} asChild><FaTrophy /></Icon>;
    }
    if (variant === 'clasico') {
      return (
        <Flex w="52px" h="52px" rounded="full" bg="white" borderWidth="2px" borderColor="gray.200" align="center" justify="center" flexShrink={0}>
          <Icon color={primaryColor} boxSize={6} asChild><FaTrophy /></Icon>
        </Flex>
      );
    }
    if (variant === 'aurora') {
      return (
        <Flex
          w="72px"
          h="72px"
          rounded="full"
          align="center"
          justify="center"
          flexShrink={0}
          bg={primaryColor}
          boxShadow={`0 8px 28px -6px ${primaryColor}aa`}
        >
          <Icon color="gray.900" boxSize={8} asChild><FaTrophy /></Icon>
        </Flex>
      );
    }
    if (variant === 'vortex') {
      return (
        <Flex
          w="72px"
          h="72px"
          rounded="full"
          align="center"
          justify="center"
          flexShrink={0}
          bg={primaryColor}
          boxShadow={`0 12px 32px -8px ${primaryColor}99`}
        >
          <Icon color="gray.900" boxSize={8} asChild><FaTrophy /></Icon>
        </Flex>
      );
    }
    if (variant === 'glass') {
      return (
        <Flex w="72px" h="72px" rounded="full" align="center" justify="center" flexShrink={0} bg="whiteAlpha.700" borderWidth="1px" borderColor="whiteAlpha.800" backdropFilter="blur(8px)">
          <Icon color={primaryColor} boxSize={8} asChild><FaTrophy /></Icon>
        </Flex>
      );
    }
    if (variant === 'kinetic') {
      return (
        <Flex w="72px" h="72px" rounded="full" align="center" justify="center" flexShrink={0} bg={primaryColor} boxShadow={`0 8px 26px -8px ${primaryColor}aa`}>
          <Icon color="white" boxSize={8} asChild><FaTrophy /></Icon>
        </Flex>
      );
    }
    if (variant === 'editorial') {
      return (
        <Flex w="56px" h="56px" rounded="none" bg={primaryColor} align="center" justify="center" flexShrink={0} color="white">
          <Icon boxSize={6} asChild><FaTrophy /></Icon>
        </Flex>
      );
    }
    if (variant === 'pulso') {
      return (
        <Flex w="64px" h="64px" rounded="none" bg={primaryColor} border="3px solid black" align="center" justify="center" flexShrink={0}>
          <Icon color="black" boxSize={7} asChild><FaTrophy /></Icon>
        </Flex>
      );
    }
    return (
      <Flex
        w="72px"
        h="72px"
        rounded="full"
        align="center"
        justify="center"
        flexShrink={0}
        bg={primaryColor}
        boxShadow={`0 12px 32px -8px ${primaryColor}99`}
      >
        <Icon color="white" boxSize={8} asChild><FaTrophy /></Icon>
      </Flex>
    );
  };

  const titleColor =
    variant === 'aurora' || variant === 'vortex' ? 'gray.50' : variant === 'elegante' ? 'gray.800' : 'gray.900';
  const descColor =
    variant === 'aurora' || variant === 'vortex' ? 'gray.400' : variant === 'elegante' ? 'gray.500' : 'gray.600';

  const cardProps = (() => {
    const base = {
      role: 'group',
      textAlign: 'center',
      align: 'center',
      spacing: 5,
      px: { base: 6, md: 8 },
      py: { base: 7, md: 8 },
      maxW: list.length === 1 ? 'min(100%, 440px)' : 'full',
      mx: list.length === 1 ? 'auto' : undefined,
      w: 'full',
      transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease',
    };
    if (variant === 'clasico') {
      return {
        ...base,
        bg: surfaceColor || 'gray.50',
        rounded: 'lg',
        borderWidth: '1px',
        borderColor: 'gray.200',
        _hover: { shadow: 'md', borderColor: 'gray.300' },
      };
    }
    if (variant === 'elegante') {
      return {
        ...base,
        bg: 'white',
        rounded: 'none',
        borderTop: '1px solid',
        borderBottom: '1px solid',
        borderColor: 'gray.200',
        py: 10,
        _hover: { borderColor: 'gray.400' },
      };
    }
    if (variant === 'aurora') {
      return {
        ...base,
        bg: 'gray.800',
        rounded: 'xl',
        borderWidth: '1px',
        borderColor: 'whiteAlpha.100',
        boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset',
        _hover: {
          borderColor: 'whiteAlpha.200',
          boxShadow: `0 12px 40px -12px ${primaryColor}44`,
          transform: 'translateY(-3px)',
        },
      };
    }
    if (variant === 'vortex') {
      return {
        ...base,
        bg: 'gray.800',
        rounded: '2xl',
        borderWidth: '1px',
        borderColor: 'whiteAlpha.100',
        boxShadow: '0 0 0 1px rgba(34,211,238,0.08) inset',
        _hover: {
          borderColor: 'whiteAlpha.300',
          boxShadow: `0 14px 44px -14px ${primaryColor}66`,
          transform: 'translateY(-3px)',
        },
      };
    }
    if (variant === 'glass') {
      return {
        ...base,
        bg: 'whiteAlpha.650',
        rounded: '2xl',
        borderWidth: '1px',
        borderColor: 'whiteAlpha.800',
        backdropFilter: 'blur(10px)',
        _hover: {
          borderColor: `${primaryColor}44`,
          boxShadow: `0 12px 30px -14px ${primaryColor}44`,
          transform: 'translateY(-2px)',
        },
      };
    }
    if (variant === 'kinetic') {
      return {
        ...base,
        bg: 'white',
        rounded: 'xl',
        borderWidth: '1px',
        borderColor: 'gray.200',
        _hover: {
          borderColor: `${primaryColor}66`,
          boxShadow: `0 14px 30px -14px ${primaryColor}77`,
          transform: 'translateY(-4px)',
        },
      };
    }
    if (variant === 'editorial') {
      return {
        ...base,
        bg: 'white',
        rounded: 'none',
        borderWidth: '1px',
        borderColor: 'gray.200',
        borderLeftWidth: '4px',
        borderLeftColor: primaryColor,
        shadow: 'sm',
        _hover: { shadow: 'md' },
      };
    }
    if (variant === 'pulso') {
      return {
        ...base,
        bg: 'white',
        rounded: 'none',
        borderWidth: '3px',
        borderColor: 'black',
        boxShadow: '6px 6px 0 #000',
        _hover: { transform: 'translate(2px, 2px)', boxShadow: '4px 4px 0 #000' },
      };
    }
    return {
      ...base,
      bg: 'white',
      rounded: '2xl',
      borderWidth: '1px',
      borderColor: 'gray.100',
      shadow: '0 4px 24px rgba(0,0,0,0.06)',
      _hover: {
        shadow: `0 16px 48px -12px ${primaryColor}33`,
        borderColor: `${primaryColor}55`,
        transform: 'translateY(-4px)',
      },
    };
  })();

  return (
    <SimpleGrid columns={{ base: 1, md: mdCols }} gap={{ base: 5, md: 6 }} maxW="920px" mx="auto" w="full">
      {list.map((l, i) => (
        <VStack key={i} {...cardProps}>
          {renderTrophyIcon()}
          <VStack gap={2} align="center" px={1}>
            <HStack justify="center" flexWrap="wrap" gap={2} align="center">
              <Text
                fontWeight={variant === 'elegante' ? '500' : '800'}
                fontSize={{ base: 'md', md: 'lg' }}
                color={titleColor}
                lineHeight="short"
              >
                {l.titulo}
              </Text>
              {yearBadge(l.ano)}
            </HStack>
            {l.descripcion ? (
              <Text fontSize="sm" color={descColor} lineHeight="tall" maxW="prose">
                {l.descripcion}
              </Text>
            ) : null}
          </VStack>
        </VStack>
      ))}
    </SimpleGrid>
  );
}
